import { Entity, UniqueEntityID } from '@domain/core'

import { AircraftType, EngineType, FuelType } from '@/domain/protocols/AircraftType'
import { AltitudeUnit, FuelConsumptionUnit, RateUnit, SpeedUnit } from '@/domain/protocols/Unit'

export interface AircraftProps {
  aircraftModelId?: UniqueEntityID
  defaultWbProfileId?: UniqueEntityID | null
  prefix: string
  aircraftType?: AircraftType
  model: string
  designatorIcao?: string
  /** Ausente quando a aeronave não o declara — a coluna chega vazia para boa parte delas (PLA-335). */
  fuelType?: FuelType | null
  engineType?: EngineType
  engineModel?: string
  speedUnit: SpeedUnit
  rateUnit: RateUnit
  fuelConsumptionUnit: FuelConsumptionUnit
  operationalCeilingUnit: AltitudeUnit
  fuelConsumptionAc: number
  fuelConsumptionCruise: number
  fuelConsumptionDc: number
  operationalCeiling: number
  rateAc: number
  rateDc: number
  speedAc: number
  speedDc: number
  speedCruise: number
  fuelConsumptionTaxi?: number

  createdAt: Date
  updatedAt: Date
  deletedAt?: Date
}

export interface AircraftPojo extends AircraftProps {
  id: string
}

export class Aircraft extends Entity<AircraftProps, AircraftPojo> {
  private constructor(props: AircraftProps, id?: UniqueEntityID) {
    super(props, id)
  }

  public static create(props: AircraftProps, id?: UniqueEntityID): Aircraft {
    const aircraft = new Aircraft(props, id)

    return aircraft
  }

  public equals(aircraft: Aircraft): boolean {
    return (
      this.props.aircraftType === aircraft.aircraftType &&
      this.props.model === aircraft.model &&
      this.props.designatorIcao === aircraft.designatorIcao &&
      this.props.speedCruise === aircraft.speedCruise &&
      this.props.speedUnit === aircraft.speedUnit &&
      this.props.engineType === aircraft.engineType &&
      this.props.engineModel === aircraft.engineModel &&
      this.props.fuelConsumptionUnit === aircraft.fuelConsumptionUnit &&
      this.props.fuelConsumptionAc === aircraft.fuelConsumptionAc &&
      this.props.fuelConsumptionCruise === aircraft.fuelConsumptionCruise &&
      this.props.fuelConsumptionDc === aircraft.fuelConsumptionDc &&
      this.props.fuelType === aircraft.fuelType &&
      this.props.operationalCeiling === aircraft.operationalCeiling &&
      this.props.operationalCeilingUnit === aircraft.operationalCeilingUnit &&
      this.props.prefix === aircraft.prefix &&
      this.props.rateAc === aircraft.rateAc &&
      this.props.rateUnit === aircraft.rateUnit &&
      this.props.rateDc === aircraft.rateDc &&
      this.props.speedAc === aircraft.speedAc &&
      this.props.speedDc === aircraft.speedDc &&
      this.props.aircraftModelId === aircraft.aircraftModelId &&
      this.props.fuelConsumptionTaxi === aircraft.fuelConsumptionTaxi
    )
  }

  public get completed(): boolean {
    const fieldsAreFilled = [
      this.props.model?.length > 0,
      this.props.prefix?.length > 0,
      !!this.props.speedAc,
      !!this.props.rateAc,
      !!this.props.fuelConsumptionAc,
      !!this.props.speedCruise,
      !!this.props.fuelConsumptionCruise,
      !!this.props.operationalCeiling,
      !!this.props.speedDc,
      !!this.props.rateDc,
      !!this.props.fuelConsumptionDc
    ].every(Boolean)

    return fieldsAreFilled
  }

  public get createdAt(): Date {
    return this.props.createdAt
  }

  public set createdAt(createdAt: Date) {
    this.props.createdAt = createdAt
  }

  public get updatedAt(): Date {
    return this.props.updatedAt
  }

  public set updatedAt(updatedAt: Date) {
    this.props.updatedAt = updatedAt
  }

  public get deletedAt(): Date {
    return this.props.deletedAt
  }

  public set deletedAt(deletedAt: Date) {
    this.props.deletedAt = deletedAt
  }

  public get aircraftModelId(): UniqueEntityID {
    return this.props.aircraftModelId
  }

  public set aircraftModelId(aircraftModelId: UniqueEntityID) {
    this.props.aircraftModelId = aircraftModelId
  }

  public get defaultWbProfileId(): UniqueEntityID | null {
    return this.props.defaultWbProfileId
  }

  public set defaultWbProfileId(defaultWbProfileId: UniqueEntityID | null) {
    this.props.defaultWbProfileId = defaultWbProfileId
  }

  public get prefix(): string {
    return this.props.prefix
  }

  public set prefix(prefix: string) {
    this.props.prefix = prefix
  }

  public get model(): string {
    return this.props.model
  }

  public set model(model: string) {
    this.props.model = model
  }

  public get aircraftType(): AircraftType {
    return this.props.aircraftType
  }

  public set aircraftType(aircraftType: AircraftType) {
    this.props.aircraftType = aircraftType
  }

  public get engineType(): EngineType {
    return this.props.engineType
  }

  public set engineType(engineType: EngineType) {
    this.props.engineType = engineType
  }

  public get engineModel(): string {
    return this.props.engineModel
  }

  public set engineModel(engineModel: string) {
    this.props.engineModel = engineModel
  }

  public get designatorIcao(): string {
    return this.props.designatorIcao
  }

  public set designatorIcao(designatorIcao: string) {
    this.props.designatorIcao = designatorIcao
  }

  public get fuelType(): FuelType | null {
    return this.props.fuelType
  }

  public set fuelType(fuelType: FuelType) {
    this.props.fuelType = fuelType
  }

  public get speedUnit(): SpeedUnit {
    return this.props.speedUnit
  }

  public set speedUnit(speedUnit: SpeedUnit) {
    this.props.speedUnit = speedUnit
  }

  public get speedAc(): number {
    return this.props.speedAc
  }

  public set speedAc(speedAc: number) {
    this.props.speedAc = speedAc
  }

  public get speedCruise(): number {
    return this.props.speedCruise
  }

  public set speedCruise(speedCruise: number) {
    this.props.speedCruise = speedCruise
  }

  public get speedDc(): number {
    return this.props.speedDc
  }

  public set speedDc(speedDc: number) {
    this.props.speedDc = speedDc
  }

  public get rateUnit(): RateUnit {
    return this.props.rateUnit
  }

  public set rateUnit(rateUnit: RateUnit) {
    this.props.rateUnit = rateUnit
  }

  public get rateAc(): number {
    return this.props.rateAc
  }

  public set rateAc(rateAc: number) {
    this.props.rateAc = rateAc
  }

  public get rateDc(): number {
    return this.props.rateDc
  }

  public set rateDc(rateDc: number) {
    this.props.rateDc = rateDc
  }

  public get fuelConsumptionTaxi(): number {
    return this.props.fuelConsumptionTaxi
  }

  public set fuelConsumptionTaxi(fuelConsumptionTaxi: number) {
    this.props.fuelConsumptionTaxi = fuelConsumptionTaxi
  }

  public get fuelConsumptionUnit(): FuelConsumptionUnit {
    return this.props.fuelConsumptionUnit
  }

  public set fuelConsumptionUnit(fuelConsumptionUnit: FuelConsumptionUnit) {
    this.props.fuelConsumptionUnit = fuelConsumptionUnit
  }

  public get fuelConsumptionAc(): number {
    return this.props.fuelConsumptionAc
  }

  public set fuelConsumptionAc(fuelConsumptionAc: number) {
    this.props.fuelConsumptionAc = fuelConsumptionAc
  }

  public get fuelConsumptionCruise(): number {
    return this.props.fuelConsumptionCruise
  }

  public set fuelConsumptionCruise(fuelConsumptionCruise: number) {
    this.props.fuelConsumptionCruise = fuelConsumptionCruise
  }

  public get fuelConsumptionDc(): number {
    return this.props.fuelConsumptionDc
  }

  public set fuelConsumptionDc(fuelConsumptionDc: number) {
    this.props.fuelConsumptionDc = fuelConsumptionDc
  }

  public get operationalCeilingUnit(): AltitudeUnit {
    return this.props.operationalCeilingUnit
  }

  public set operationalCeilingUnit(operationalCeilingUnit: AltitudeUnit) {
    this.props.operationalCeilingUnit = operationalCeilingUnit
  }

  public get operationalCeiling(): number {
    return this.props.operationalCeiling
  }

  public set operationalCeiling(operationalCeiling: number) {
    this.props.operationalCeiling = operationalCeiling
  }

  public toPojo(): AircraftPojo {
    const aircraftPojo: AircraftPojo = {
      id: this.id.toString(),
      aircraftModelId: this.props.aircraftModelId,
      defaultWbProfileId: this.props.defaultWbProfileId,
      prefix: this.props.prefix,
      model: this.props.model,
      designatorIcao: this.props.designatorIcao,
      fuelType: this.props.fuelType,
      aircraftType: this.props.aircraftType,
      engineType: this.props.engineType,
      engineModel: this.props.engineModel,
      speedUnit: this.props.speedUnit,
      rateUnit: this.props.rateUnit,
      fuelConsumptionUnit: this.props.fuelConsumptionUnit,
      operationalCeilingUnit: this.props.operationalCeilingUnit,
      fuelConsumptionAc: this.props.fuelConsumptionAc,
      fuelConsumptionCruise: this.props.fuelConsumptionCruise,
      fuelConsumptionDc: this.props.fuelConsumptionDc,
      operationalCeiling: this.props.operationalCeiling,
      rateAc: this.props.rateAc,
      rateDc: this.props.rateDc,
      speedAc: this.props.speedAc,
      speedCruise: this.props.speedCruise,
      speedDc: this.props.speedDc,
      fuelConsumptionTaxi: this.props.fuelConsumptionTaxi,
      createdAt: this.props.createdAt,
      updatedAt: this.props.updatedAt,
      deletedAt: this.props.deletedAt
    }

    return aircraftPojo
  }

  public static fromPojo(pojo: AircraftPojo): Aircraft {
    if (!pojo) return null

    const routeProps: AircraftProps = {
      aircraftModelId: pojo.aircraftModelId,
      defaultWbProfileId: pojo.defaultWbProfileId,
      prefix: pojo.prefix,
      model: pojo.model,
      designatorIcao: pojo.designatorIcao,
      fuelType: pojo.fuelType,
      aircraftType: pojo.aircraftType,
      engineType: pojo.engineType,
      engineModel: pojo.engineModel,
      rateUnit: pojo.rateUnit,
      speedUnit: pojo.speedUnit,
      fuelConsumptionUnit: pojo.fuelConsumptionUnit,
      operationalCeilingUnit: pojo.operationalCeilingUnit,
      fuelConsumptionAc: pojo.fuelConsumptionAc,
      fuelConsumptionCruise: pojo.fuelConsumptionCruise,
      fuelConsumptionDc: pojo.fuelConsumptionDc,
      operationalCeiling: pojo.operationalCeiling,
      rateAc: pojo.rateAc,
      rateDc: pojo.rateDc,
      speedAc: pojo.speedAc,
      speedCruise: pojo.speedCruise,
      speedDc: pojo.speedDc,
      fuelConsumptionTaxi: pojo.fuelConsumptionTaxi,
      createdAt: pojo.createdAt,
      updatedAt: pojo.updatedAt,
      deletedAt: pojo.deletedAt
    }

    return new Aircraft(routeProps, new UniqueEntityID(pojo.id))
  }
}
